import axios from 'axios'
import { useCallback } from 'react'

import { samApiClient } from '@/api/index'
import useNonConcurrentRequest, {
  downloadFile,
  setPaginationParameters,
} from '@/api/Util'
import type { Device } from '@/pages/Device/Device'
import type {
  DurationTestReport,
  RcsSelfTestReport,
  RdiSelfTestReport,
  RssSelfTestReport,
  ZklSelfTestReport,
} from '@/pages/Device/Reports/ReportDefinitions'

import { DurationTestStatus } from './definition'
import type { PageOptions, PageResult } from './definition'

export const useFetchRdiReportPage = function () {
  const ncr = useNonConcurrentRequest(samApiClient)

  return {
    fetchReportPage: async function (
      uid: Device['uid'],
      { pageIndex, pageSize, sortBy, globalFilter }: PageOptions,
    ): Promise<PageResult<RdiSelfTestReport[]>> {
      const params = setPaginationParameters({
        pageIndex,
        pageSize,
        sortBy,
        globalFilter,
      })

      const url = `rdi/${uid}/report?${params.toString()}`
      try {
        const response = await ncr.request<RdiSelfTestReport[]>({
          url,
          method: 'GET',
        })
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
  }
}

export const useFetchRssReportPage = function () {
  const ncr = useNonConcurrentRequest(samApiClient)

  return {
    fetchReportPage: async function (
      uid: Device['uid'],
      { pageIndex, pageSize, sortBy, globalFilter }: PageOptions,
    ): Promise<PageResult<RssSelfTestReport[]>> {
      const params = setPaginationParameters({
        pageIndex,
        pageSize,
        sortBy,
        globalFilter,
      })

      const url = `rss/${uid}/report?${params.toString()}`
      try {
        const response = await ncr.request<RssSelfTestReport[]>({
          url,
          method: 'GET',
        })
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
  }
}

export const useFetchZklReportPage = function () {
  const ncr = useNonConcurrentRequest(samApiClient)

  const fetchReportPage = useCallback(
    async function (
      uid: Device['uid'],
      { pageIndex, pageSize, sortBy, globalFilter }: PageOptions,
    ): Promise<PageResult<ZklSelfTestReport[]>> {
      const params = setPaginationParameters({
        pageIndex,
        pageSize,
        sortBy,
        globalFilter,
      })

      const url = `zkl-rc/${uid}/report?${params.toString()}`
      try {
        const response = await ncr.request<ZklSelfTestReport[]>({
          url,
          method: 'GET',
        })
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
    [ncr],
  )

  return { fetchReportPage }
}

export const useFetchDeviceDurationTestReportPage = function () {
  const ncr = useNonConcurrentRequest(samApiClient)

  return {
    fetchDurationTestReportPage: async function (
      uid: Device['uid'],
      { pageIndex, pageSize, sortBy, globalFilter }: PageOptions,
    ): Promise<PageResult<DurationTestReport[]>> {
      const params = setPaginationParameters({
        pageIndex,
        pageSize,
        sortBy,
        globalFilter,
      })

      params.set(
        'status',
        [
          DurationTestStatus.DurationTestIgnored,
          DurationTestStatus.DurationTestSkipped,
          DurationTestStatus.DurationTestCompleted,
        ].join(','),
      )

      const url = `device/${uid}/duration-test-reports?${params.toString()}`

      try {
        const response = await ncr.request<DurationTestReport[]>({
          url,
          method: 'GET',
        })

        console.log('Response headers:', response.headers)
        console.log('X-Total-Count header:', response.headers['x-total-count'])
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
  }
}

export async function downloadZklRcReport(
  uid: string,
  reportID: number,
  language: string,
): Promise<void> {
  try {
    const params = new URLSearchParams()
    params.set('lang', language)

    await downloadFile(
      `zkl-rc/${uid}/report/${reportID}?${params.toString()}`,
      samApiClient,
    )
  } catch {
    // Caught by interceptor
  }
}

export async function downloadRssReport(
  uid: string,
  reportID: number,
  language: string,
): Promise<void> {
  try {
    const params = new URLSearchParams()
    params.set('lang', language)

    await downloadFile(
      `rss/${uid}/report/${reportID}?${params.toString()}`,
      samApiClient,
    )
  } catch {
    // Caught by interceptor
  }
}

export async function downloadRdiReport(
  uid: string,
  reportID: number,
  language: string,
): Promise<void> {
  try {
    const params = new URLSearchParams()
    params.set('lang', language)

    await downloadFile(
      `rdi/${uid}/report/${reportID}?${params.toString()}`,
      samApiClient,
    )
  } catch {
    // Caught by interceptor
  }
}

export async function downloadDeviceDurationTestReport(
  uid: string,
  reportID: number,
  language: string,
): Promise<void> {
  try {
    const params = new URLSearchParams()
    params.set('lang', language)

    await downloadFile(
      `device/${uid}/duration-report/${reportID}?${params.toString()}`,
      samApiClient,
    )
  } catch {
    // Caught by interceptor
  }
}

export const useFetchRcsReportPage = function () {
  const ncr = useNonConcurrentRequest(samApiClient)

  return {
    fetchReportPage: async function (
      uid: Device['uid'],
      { pageIndex, pageSize, sortBy, globalFilter }: PageOptions,
    ): Promise<PageResult<RcsSelfTestReport[]>> {
      const params = setPaginationParameters({
        pageIndex,
        pageSize,
        sortBy,
        globalFilter,
      })

      const url = `rcs/${uid}/report?${params.toString()}`
      try {
        const response = await ncr.request<RcsSelfTestReport[]>({
          url,
          method: 'GET',
        })
        return {
          rowCount: Number(response.headers['x-total-count']),
          data: response.data,
        }
      } catch (e) {
        if (!axios.isCancel(e)) throw e
        return {
          rowCount: null,
          data: null,
        }
      }
    },
  }
}

export async function downloadRcsReport(
  uid: string,
  reportID: number,
  language: string,
): Promise<void> {
  try {
    const params = new URLSearchParams()
    params.set('lang', language)

    await downloadFile(
      `rcs/${uid}/report/${reportID}?${params.toString()}`,
      samApiClient,
    )
  } catch {
    // Caught by interceptor
  }
}
